import { Breadcrumbs } from '@primer/react';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { tabletOrNarrower } from '../../../common/generic/layout/breakpoint-utils';
import { appHeaderHeight, INTERNAL_BREADCRUMBS_HEIGHT_PX } from '../../../common/generic/layout/layout-constants';

interface Props {
  items: Item[];
  /** Optional content rendered right-aligned in the bar, e.g. a "Create…" menu. */
  actions?: ReactNode;
  /**
   * Background color of the bar (any CSS color / custom property). Defaults to
   * the standard internal blue. Pages override it to make it obvious at a
   * glance which internal tool the analyst is in — e.g. the reasoning editor
   * uses purple so it can't be confused with the blue blacklist view (PLT-5369).
   */
  backgroundColor?: string;
}

interface Item {
  label: string;
  link?: string;
  /** Optional content rendered inline immediately after this crumb's label. */
  adornment?: ReactNode;
}

/**
 * Top-of-page breadcrumb bar rendered on every page under /internal.
 *
 * Layout: a fixed, full-width bar pinned directly below the global header.
 * It spans the whole viewport width even on pages with a sidebar (the sidebar
 * is itself fixed and starts below this bar via `hasTitleBar` on
 * `BouPageWithFrame`). Modeled after the entity TitleBar.
 *
 * The breadcrumb bar acts as the page title — pages should not render a
 * separate H1 that duplicates the breadcrumb label.
 *
 * The root crumb "Upright internal" is plain text (not a link): there is no
 * landing page under /internal anymore.
 */
export function InternalBreadcrumbs({ items, actions, backgroundColor }: Props) {
  return (
    <Root style={backgroundColor ? { backgroundColor } : undefined}>
      <Inner>
        <Breadcrumbs>
          <Breadcrumbs.Item href="#" selected>
            <RootLabel>Upright internal</RootLabel>
          </Breadcrumbs.Item>
          {items.map(item => (
            <Breadcrumbs.Item key={item.label}>
              {item.adornment ? (
                <CrumbWithAdornment>
                  {item.link ? <Link to={item.link}>{item.label}</Link> : item.label}
                  {item.adornment}
                </CrumbWithAdornment>
              ) : item.link ? (
                <Link to={item.link}>{item.label}</Link>
              ) : (
                item.label
              )}
            </Breadcrumbs.Item>
          ))}
        </Breadcrumbs>
        {actions && <Actions>{actions}</Actions>}
      </Inner>
    </Root>
  );
}

const Root = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: ${appHeaderHeight};
  z-index: 90;
  height: ${INTERNAL_BREADCRUMBS_HEIGHT_PX};
  background-color: var(--color__blue-100);
  border-bottom: 1px solid var(--borderColor-default);
  display: flex;
  align-items: center;
  padding: 0 var(--space__lg);
  font-size: var(--font-size__lg);

  a {
    color: var(--platform-gray-black);
    text-decoration: none !important;

    &:hover {
      color: var(--platform-blue-700);
    }
  }

  ${tabletOrNarrower(css`
    position: static;
    height: auto;
    padding: var(--space__sm) var(--space__lg);
  `)}
`;

const Inner = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space__lg);
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__sm);
  flex-shrink: 0;
`;

const CrumbWithAdornment = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space__xs);
`;

const RootLabel = styled.b`
  /* Plain text: there is no landing page for /internal anymore. */
  cursor: default;
`;
